const BACKUP_LOCK_NAME = 'variorum-app-state-backup-v1'

type LockManagerLike = {
  request: <T>(
    name: string,
    options: { mode: 'shared' | 'exclusive' },
    callback: () => Promise<T>
  ) => Promise<T>
}

const pendingWrites = new Set<Promise<unknown>>()
let fallbackGate: Promise<void> = Promise.resolve()
let releaseFallbackGate: (() => void) | null = null
let fallbackExclusiveTail: Promise<void> = Promise.resolve()
let sameDocumentExclusiveOperationActive = false

export const BACKUP_OPERATION_BUSY_MESSAGE = 'Another backup or restore is already running.'

const getLockManager = (): LockManagerLike | null => {
  const locks = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { locks?: LockManagerLike }).locks
    : undefined
  return locks && typeof locks.request === 'function' ? locks : null
}

const trackPendingWrite = async <T>(operation: () => Promise<T>): Promise<T> => {
  const operationPromise = operation()
  pendingWrites.add(operationPromise)
  try {
    return await operationPromise
  } finally {
    pendingWrites.delete(operationPromise)
  }
}

export const runBackupRelevantWrite = async <T>(operation: () => Promise<T>): Promise<T> => {
  const locks = getLockManager()
  if (locks) {
    return locks.request(BACKUP_LOCK_NAME, { mode: 'shared' }, () => trackPendingWrite(operation))
  }
  await fallbackGate
  return trackPendingWrite(operation)
}

const drainPendingWrites = async (): Promise<void> => {
  while (pendingWrites.size > 0) {
    const results = await Promise.allSettled(Array.from(pendingWrites))
    const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
    if (failed) {
      throw failed.reason instanceof Error
        ? failed.reason
        : new Error('A pending application write failed before backup could begin.')
    }
  }
}

export const runExclusiveBackupOperation = async <T>(operation: () => Promise<T>): Promise<T> => {
  if (sameDocumentExclusiveOperationActive) {
    throw new Error(BACKUP_OPERATION_BUSY_MESSAGE)
  }
  sameDocumentExclusiveOperationActive = true

  try {
    const locks = getLockManager()
    if (locks) {
      return await locks.request(BACKUP_LOCK_NAME, { mode: 'exclusive' }, async () => {
        await drainPendingWrites()
        return operation()
      })
    }

    const previousExclusive = fallbackExclusiveTail
    let releaseExclusive!: () => void
    fallbackExclusiveTail = new Promise<void>(resolve => { releaseExclusive = resolve })
    await previousExclusive
    fallbackGate = new Promise<void>(resolve => { releaseFallbackGate = resolve })
    try {
      await drainPendingWrites()
      return await operation()
    } finally {
      releaseFallbackGate?.()
      releaseFallbackGate = null
      fallbackGate = Promise.resolve()
      releaseExclusive()
    }
  } finally {
    sameDocumentExclusiveOperationActive = false
  }
}
